import { useEffect, useRef, useState } from 'react';
import { retrieveIdentity } from '../lib/identity/crossDomainIdentity';
import { Countries, parseMsisdn, prettyPrintMsisdn } from '../core/util/msisdn';
import { initIframeResizeReporter } from '../core/util/iframeResize';
import { realIframe } from '../platform/iframe/realIframe';
import { env } from '../core/util/env';
import { useLanguageContext } from '../core/locale/LanguageContext';

/**
 * Minimal page hosted on the Vipps origin and embedded as an iframe by the widget SDK on desktop.
 * It renders the real <vipps-mobilepay-button> web component and, on click, tells the parent SDK to
 * open the payment overlay. (On mobile the SDK skips this iframe and renders the button directly on
 * the merchant page so the app switch stays on the tap's user gesture.)
 *
 * Because it runs on the Vipps origin it can also read the remembered number (same-merchant
 * partitioned localStorage, or the .vipps.no cookie via the Storage Access API) and render the
 * button as "Continue as <number>".
 */

const BUTTON_COMPONENT_SCRIPT_ID = 'vipps-button-component';

const buttonComponentUrl = (): string => {
  if (env.PROD) return 'https://ecomelementsprod.z6.web.core.windows.net/button/button.js';
  if (env.MT) return 'https://ecomelementsmt.z6.web.core.windows.net/button/button.js';
  return 'https://ecomelementsuat.z6.web.core.windows.net/button/button.js';
};

const ensureButtonComponentLoaded = (): void => {
  if (
    document.getElementById(BUTTON_COMPONENT_SCRIPT_ID) ||
    customElements.get('vipps-mobilepay-button')
  ) {
    return;
  }
  const script = document.createElement('script');
  script.id = BUTTON_COMPONENT_SCRIPT_ID;
  script.async = true;
  script.src = buttonComponentUrl();
  document.body.appendChild(script);
};

function WidgetButtonView(): React.ReactNode {
  const hostRef = useRef<HTMLDivElement>(null);
  const [displayNumber, setDisplayNumber] = useState<string | null>(null);
  const languageContext = useLanguageContext();

  // Read the remembered number once on load (gesture-free; only returns something for returning users).
  useEffect(() => {
    let cancelled = false;
    void retrieveIdentity({ phase: 'eager' }).then((res) => {
      if (cancelled || !res.number) {
        return;
      }
      const msisdn = parseMsisdn(res.country ?? Countries.Norway, res.number);
      setDisplayNumber(prettyPrintMsisdn(msisdn));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    ensureButtonComponentLoaded();
    const host = hostRef.current;
    if (!host) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const stretched = (params.get('stretched') ?? 'false') === 'true';
    const disabled = (params.get('disabled') ?? 'false') === 'true';
    const loading = (params.get('loading') ?? 'false') === 'true';

    document.documentElement.style.margin = '0';
    document.body.style.margin = '0';
    if (stretched) {
      // Fill the full-width iframe the SDK renders for stretched buttons.
      host.style.display = 'block';
      host.style.width = '100%';
    } else {
      // Let the iframe content size to the button's natural width so the resize reporter measures the
      // un-wrapped width (otherwise the fixed-width iframe could make the label wrap).
      host.style.display = 'inline-block';
      host.style.width = 'max-content';
      host.style.whiteSpace = 'nowrap';
    }

    const postClick = (mode: 'continue-as' | 'different-number' | 'default') =>
      window.parent.postMessage({ source: 'vipps-widget-sdk', event: 'button-clicked', mode }, '*');

    const button = document.createElement('vipps-mobilepay-button');
    button.setAttribute('brand', params.get('brand') ?? 'vipps');
    button.setAttribute('language', params.get('language') ?? 'no');
    button.setAttribute('verb', params.get('verb') ?? 'pay');
    button.setAttribute('variant', params.get('variant') ?? 'primary');
    button.setAttribute('type', params.get('type') ?? 'button');
    button.setAttribute('compact', params.get('compact') ?? 'false');
    button.setAttribute('rounded', params.get('rounded') ?? 'true');
    button.setAttribute('stretched', String(stretched));
    // Set only when true: <vipps-mobilepay-button> is form-associated, so a present disabled
    // attribute disables it natively no matter the value.
    if (disabled) button.setAttribute('disabled', 'true');
    if (loading) button.setAttribute('loading', 'true');
    const continueAsFirstName = params.get('continueAsFirstName');
    if (continueAsFirstName) button.setAttribute('vmpcontinueasfirstname', continueAsFirstName);

    if (displayNumber) {
      button.setAttribute('verb', 'continue');
      button.setAttribute('vmpcontinueasfirstname', displayNumber);
    }
    const onPrimaryClick = async () => {
      if (displayNumber) {
        postClick('continue-as');
        return;
      }
      // Regular button: the web-component click is a real gesture (no delay), so request storage
      // access now and let retrieveIdentity mirror the cross-domain number into the (merchant, vipps)
      // partitioned localStorage. Then open the overlay — it reads that number gesture-free on load
      // and shows "Continue as"/"Use another number" itself (works in Firefox; no in-place reveal).
      await retrieveIdentity({ phase: 'gesture' });
      postClick('default');
    };
    button.addEventListener('click', onPrimaryClick);

    let useAnotherButton: HTMLButtonElement | null = null;
    if (displayNumber) {
      useAnotherButton = document.createElement('button');
      useAnotherButton.type = 'button';
      useAnotherButton.className = 'widget-button-use-another';
      useAnotherButton.textContent = languageContext.getLocalizedText('useAnotherNumberLabel');
      useAnotherButton.disabled = disabled || loading;
      useAnotherButton.style.cssText =
        'display:block;margin:8px auto 0;padding:4px 8px;background:none;border:none;' +
        'font:inherit;color:inherit;text-decoration:underline;cursor:pointer;';
      useAnotherButton.addEventListener('click', () => postClick('different-number'));
      host.replaceChildren(button, useAnotherButton);
    } else {
      host.replaceChildren(button);
    }

    // The parent SDK toggles the button's loading state while the overlay is opening.
    const onParentMessage = (event: MessageEvent) => {
      const data = event.data as { source?: string; event?: string; loading?: boolean };
      if (data?.source !== 'vipps-widget-sdk-parent' || data.event !== 'set-loading') {
        return;
      }
      if (data.loading) {
        button.setAttribute('loading', 'true');
      } else {
        button.removeAttribute('loading');
      }
    };
    window.addEventListener('message', onParentMessage);

    const stopResizeReporter = initIframeResizeReporter(host, realIframe);

    return () => {
      button.removeEventListener('click', onPrimaryClick);
      window.removeEventListener('message', onParentMessage);
      stopResizeReporter();
      host.replaceChildren();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-render only when the masked number changes
  }, [displayNumber]);

  return <div ref={hostRef} className="widget-button-host" />;
}

export default WidgetButtonView;
